/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { AlertDialog } from '../../../../shared/Dialog/AlertDialog';
import { deletePasswordPolicy } from '../../../../services/setting';
import { selectPasswordPolicies } from '../../../../store/passwordPolicies/passwordPoliciesSelector';
import { fetchPasswordPolicies } from '../../../../store/passwordPolicies/passwordPolicies';
import { displaySnackbar } from '../../../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../../../utility/stringInterpolation';
import {
  DELETE_PASSWORD_POLICY,
  DELETE_PASSWORD_POLICY_CONTENT,
  DELETE,
  CANCEL_BTN_TEXT,
  DELETE_PASSWORD_POLICY_SUCCESS,
  DELETE_PASSWORD_POLICY_ERROR
} from './constants';
import { PasswordPolicyModel } from '../../../../models/PasswordPolicy/PasswordPolicy';
import { Typography } from '@mui/material';

interface DeleteDialogProps {
  passwordPolicyId: string;
  onClose: () => void;
}

const DeleteDialog = ({ passwordPolicyId, onClose }: DeleteDialogProps) => {
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const dispatch = useDispatch();
  const [isRequesting, setIsRequesting] = useState(false);

  const passwordPolicy = passwordPolicies.find(
    (passwordPolicy: PasswordPolicyModel) => passwordPolicy.id === passwordPolicyId
  );

  const handleSubmit = async () => {
    setIsRequesting(true);

    try {
      await deletePasswordPolicy(passwordPolicyId);
      fetchPasswordPolicies(dispatch);
      dispatch(displaySnackbar(interpolateString([passwordPolicy.name], DELETE_PASSWORD_POLICY_SUCCESS), 'success'));
    } catch (error: any) {
      dispatch(displaySnackbar(`${DELETE_PASSWORD_POLICY_ERROR}: ${error}`, 'error'));
    }

    onClose();
  };

  return (
    <AlertDialog
      dialogContentText={DELETE_PASSWORD_POLICY}
      dialogTitle={DELETE_PASSWORD_POLICY_CONTENT}
      recordName={passwordPolicy.name}
      primaryButtonText={DELETE}
      secondButtonText={CANCEL_BTN_TEXT}
      handleOnCancel={onClose}
      handleOnOk={handleSubmit}
      showDialogLoader={isRequesting}
      primaryButtonColor="error"
      additionalContent={
        passwordPolicy?.idpsNames.length > 0 && (
          <Typography component={'div'}>
            <div>{'This policy is assigned to the following user stores'}</div>
            <ul>
              {passwordPolicy?.idpsNames.map((idpName: string) => (<li>{idpName.replace(/^BY /, 'Blue Yonder ')}</li>))}
            </ul>
          </Typography>)}
    />
  );
};

export default DeleteDialog;
